import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { API_URL, QUERY_KEYS } from "../constants"

export interface FilterView {
  _id: string
  name: string
  table: string
  filters: Record<string, any>
  organizationId: string
  creatorId: string
  createdAt: string
  updatedAt: string
  pinned?: boolean
}

export function useFilterViews(organizationId: string | undefined, table: string, token: string) {
  return useQuery<FilterView[]>({
    queryKey: QUERY_KEYS.filterViews(organizationId ?? "", table),
    queryFn: async () => {
      const url = `${API_URL}/organizations/${organizationId}/filter-views?table=${table}`
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      if (!res.ok) throw new Error("Failed to fetch views")
      return res.json()
    },
    enabled: !!organizationId && !!table,
  })
}

export function useCreateFilterView(token: string) {
  const queryClient = useQueryClient()
  return useMutation<FilterView, unknown, Omit<FilterView, "_id" | "creatorId" | "createdAt" | "updatedAt">>({
    mutationFn: async (payload) => {
      const res = await fetch(`${API_URL}/organizations/${payload.organizationId}/filter-views`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error("Failed to create view")
      return res.json()
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.filterViews(variables.organizationId, variables.table),
      })
    },
  })
}

export function useUpdateFilterView(organizationId: string, table: string, token: string) {
  const queryClient = useQueryClient()
  return useMutation<
    FilterView,
    unknown,
    { id: string } & Partial<Omit<FilterView, "_id" | "creatorId" | "createdAt" | "updatedAt">>
  >({
    mutationFn: async ({ id, ...payload }) => {
      const res = await fetch(`${API_URL}/organizations/${organizationId}/filter-views/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error("Failed to update view")
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.filterViews(organizationId, table) })
    },
  })
}

export function useDeleteFilterView(organizationId: string, table: string, token: string) {
  const queryClient = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/organizations/${organizationId}/filter-views/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      if (!res.ok) throw new Error("Failed to delete filter view")
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.filterViews(organizationId, table) })
    },
  })
}
